// src/pages/PartnerServicesDirectory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * Répertoire des partenaires de services Artkiv
 * - Courtiers en assurance (partner_type: 'insurance')
 * - Transporteurs d'œuvres d'art (partner_type: 'transport')
 * - Conservateurs-restaurateurs (partner_type: 'conservation')
 * - Autres services professionnels
 * 
 * Ces partenaires sont des comptes honoraires (role='partner') dans profiles
 * À distinguer de preferred_insurers/preferred_experts qui sont le carnet d'adresses personnel des clients
 */

const PartnerServicesDirectory = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'insurance', 'transport', 'conservation', 'other'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    insurance: 0,
    transport: 0,
    conservation: 0,
    other: 0
  });

  // Catégories de partenaires
  const partnerCategories = [
    { 
      key: 'all', 
      label: t('partners.categories.labels.all'),
      icon: '🏢',
      description: t('partners.categories.descriptions.all')
    },
    { 
      key: 'insurance', 
      label: t('partners.categories.labels.insurance'),
      icon: '🛡️',
      description: t('partners.categories.descriptions.insurance')
    },
    { 
      key: 'transport', 
      label: t('partners.categories.labels.transport'),
      icon: '🚚',
      description: t('partners.categories.descriptions.transport')
    },
    { 
      key: 'conservation', 
      label: t('partners.categories.labels.conservation'),
      icon: '🔬',
      description: t('partners.categories.descriptions.conservation')
    },
    { 
      key: 'other', 
      label: t('partners.categories.labels.other'),
      icon: '⭐',
      description: t('partners.categories.descriptions.other')
    }
  ];

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer tous les partenaires (role='partner' dans profiles)
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'partner')
        .order('company', { ascending: true });

      if (fetchError) throw fetchError;

      setPartners(data || []);

      // Calculer les stats
      const partnerData = data || [];
      const statsData = {
        total: partnerData.length,
        insurance: partnerData.filter(p => p.partner_type === 'insurance').length,
        transport: partnerData.filter(p => p.partner_type === 'transport').length,
        conservation: partnerData.filter(p => p.partner_type === 'conservation').length,
        other: partnerData.filter(p => !p.partner_type || !['insurance', 'transport', 'conservation'].includes(p.partner_type)).length
      };
      setStats(statsData);

    } catch (err) {
      console.error('Erreur fetchPartners:', err);
      setError(t('partners.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const applyFilters = useCallback(() => {
    let filtered = [...partners];

    // Filtre par catégorie
    if (activeFilter !== 'all') {
      if (activeFilter === 'other') {
        filtered = filtered.filter(p => 
          !p.partner_type || !['insurance', 'transport', 'conservation'].includes(p.partner_type)
        );
      } else {
        filtered = filtered.filter(p => p.partner_type === activeFilter);
      }
    }

    // Filtre par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const company = (p.company || '').toLowerCase();
        const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
        const email = (p.email || '').toLowerCase();
        const address = (p.address || '').toLowerCase();
        
        return company.includes(query) || 
               name.includes(query) || 
               email.includes(query) || 
               address.includes(query);
      });
    }

    setFilteredPartners(filtered);
  }, [partners, activeFilter, searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleContactPartner = (partner) => {
    setSelectedPartner(partner);
    setShowContactModal(true);
  };

  const handleAddToContacts = async (partner) => {
    if (!user?.id) {
      alert(t('partners.alert.loginRequired'));
      return;
    }

    try {
      // Ajouter à preferred_insurers si c'est un courtier
      if (partner.partner_type === 'insurance') {
        const { error } = await supabase
          .from('preferred_insurers')
          .insert([{
            client_id: user.id,
            name: `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || partner.company,
            company: partner.company,
            contact_email: partner.email,
            contact_phone: partner.phone,
            reputation_score: 8 // Score par défaut pour les partenaires Artkiv
          }]);

        if (error) throw error;
        alert(t('partners.alert.addedToContacts'));
        
      } else {
        // Pour les autres types, on pourrait créer une table générique ou utiliser une autre approche
        alert(t('partners.alert.comingSoon'));
      }
    } catch (err) {
      console.error('Erreur handleAddToContacts:', err);
      alert(t('partners.alert.error'));
    }
  };

  const getPartnerIcon = (partnerType) => {
    const category = partnerCategories.find(c => c.key === partnerType);
    return category?.icon || '🏢';
  };

  const getPartnerBadgeColor = (partnerType) => {
    switch (partnerType) {
      case 'insurance': return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'transport': return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'conservation': return 'bg-green-50 text-green-800 border-green-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        
        {/* Header */}
        <div className="mb-10">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('partners.back')}
          </button>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                {t('partners.title')}
              </h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl">
                {t('partners.subtitle')}
              </p>
            </div>

            {/* Barre de recherche */}
            <div className="relative max-w-md w-full md:w-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('partners.search')}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-black focus:border-transparent text-sm"
              />
              <svg 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {partnerCategories.map((category) => {
            const count = category.key === 'all' ? stats.total : stats[category.key] || 0;
            const isActive = activeFilter === category.key;
            
            return (
              <button
                key={category.key}
                onClick={() => setActiveFilter(category.key)}
                className={`group relative rounded-2xl p-5 border transition-all duration-300 text-left ${
                  isActive
                    ? 'bg-black text-white border-black shadow-lg'
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <div className={`text-2xl mb-2 transition-transform duration-300 ${
                  isActive ? 'scale-110' : 'group-hover:scale-110'
                }`}>
                  {category.icon}
                </div>
                <div className={`text-3xl font-light mb-1 ${isActive ? 'text-white' : 'text-gray-900'}`}>
                  {count}
                </div>
                <div className={`text-xs uppercase tracking-wide font-medium ${
                  isActive ? 'text-white/80' : 'text-gray-600'
                }`}>
                  {category.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Liste des partenaires */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
          {loading ? (
            <div className="py-20 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
              <p className="text-gray-500">{t('partners.loading')}</p>
            </div>
          ) : error ? (
            <div className="py-20 text-center text-red-600">
              <p>{error}</p>
            </div>
          ) : filteredPartners.length === 0 ? (
            <div className="py-20 text-center">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-gray-600 text-lg mb-2">
                {t('partners.empty.title')}
              </p>
              <p className="text-gray-500 text-sm">
                {searchQuery 
                  ? t('partners.empty.search')
                  : t('partners.empty.filter')
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPartners.map((partner) => {
                const displayName = `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || partner.company;
                
                return (
                  <div
                    key={partner.id}
                    className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
                  >
                    {/* Badge catégorie */}
                    <div className="absolute top-4 right-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getPartnerBadgeColor(partner.partner_type)}`}>
                        {getPartnerIcon(partner.partner_type)}
                        {partnerCategories.find(c => c.key === partner.partner_type)?.label || t('partners.service')}
                      </span>
                    </div>

                    {/* Icône entreprise */}
                    <div className="w-14 h-14 mb-4 rounded-xl bg-black/5 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300">
                      {getPartnerIcon(partner.partner_type)}
                    </div>

                    {/* Infos */}
                    <div className="mb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        {partner.company || displayName}
                      </h3>
                      
                      {partner.company && (partner.first_name || partner.last_name) && (
                        <p className="text-sm text-gray-600 mb-2">
                          {displayName}
                        </p>
                      )}

                      {partner.address && (
                        <p className="text-xs text-gray-500 flex items-start gap-1 mb-2">
                          <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="line-clamp-2">{partner.address}</span>
                        </p>
                      )}

                      {partner.email && (
                        <a 
                          href={`mailto:${partner.email}`}
                          className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate">{partner.email}</span>
                        </a>
                      )}

                      {partner.phone && (
                        <a 
                          href={`tel:${partner.phone}`}
                          className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {partner.phone}
                        </a>
                      )}
                    </div>

                    {/* Certifications */}
                    {partner.certifications && Object.keys(partner.certifications).length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(partner.certifications).slice(0, 3).map(([key, value], idx) => (
                            <span 
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200"
                            >
                              ✓ {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleContactPartner(partner)}
                        className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-black text-white hover:bg-gray-900 transition-colors"
                      >
                        {t('partners.contact')}
                      </button>
                      
                      <button
                        onClick={() => handleAddToContacts(partner)}
                        className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                        title={t('partners.addToContacts')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal de contact */}
        {showContactModal && selectedPartner && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-light text-gray-900">
                  {t('partners.modal.title')}
                </h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('partners.modal.desc')}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      {t('partners.modal.company')}
                    </div>
                    <div className="font-medium text-gray-900">
                      {selectedPartner.company || `${selectedPartner.first_name} ${selectedPartner.last_name}`}
                    </div>
                  </div>

                  {selectedPartner.email && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('partners.modal.email')}</div>
                      <a 
                        href={`mailto:${selectedPartner.email}`}
                        className="text-black hover:underline"
                      >
                        {selectedPartner.email}
                      </a>
                    </div>
                  )}

                  {selectedPartner.phone && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        {t('partners.modal.phone')}
                      </div>
                      <a 
                        href={`tel:${selectedPartner.phone}`}
                        className="text-black hover:underline"
                      >
                        {selectedPartner.phone}
                      </a>
                    </div>
                  )}

                  {selectedPartner.address && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        {t('partners.modal.address')}
                      </div>
                      <div className="text-gray-900">{selectedPartner.address}</div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowContactModal(false)}
                  className="w-full px-4 py-3 bg-black text-white rounded-xl hover:bg-gray-900 transition-colors text-sm font-medium"
                >
                  {t('partners.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerServicesDirectory;